import * as Sentry from "@sentry/browser";
import { PayloadType } from "Static/@cortex/messages/enums/payload_type_pb";
import { Envelope } from "Static/@cortex/messages/schemas/envelope_pb";
import { Message } from "Static/@cortex/messages/schemas/message_pb";
import { Progress } from "Static/@cortex/messages/schemas/progress_pb";
import { TaskResult } from "Static/@cortex/messages/schemas/task_result_pb";
import { VALID_MESSAGE_TYPES } from "constant/protobuf";
import { captureStack } from "integrations/sentry";

function decodePayload(decoder, payload) {
  return decoder.deserializeBinary(payload.value).toObject();
}

/*
 * Return decoded payload through a specified handler for a given type.
 * If a callback handler is not registered in msgHandlers, then messages
 * of that type will be ignored.
 *
 * @param {bytes} msg - Encoded Protobuf message.
 * @param {object} msgHandlers - An object containing optional message
 *   handlers for a given type.
 * @param {function} msgHandlers.message
 * @param {function} msgHandlers.progress
 * @param {function} msgHandlers.taskResult
 * @return {(object|undefined)}
 *   case 1: Registered handler -> message object.
 *   case 2: No handler -> undefined.
 *   case 3: Unknown message type -> undefined.
 */
function payloadRouter(msg, msgHandlers) {
  const envelope = Envelope.deserializeBinary(msg).toObject();
  const { payload, type } = envelope;

  if (msgHandlers.message && type === PayloadType.MESSAGE) {
    return msgHandlers.message(decodePayload(Message, payload));
  }

  if (msgHandlers.progress && type === PayloadType.PROGRESS) {
    return msgHandlers.progress(decodePayload(Progress, payload));
  }

  if (msgHandlers.taskResult && type === PayloadType.TASK_RESULT) {
    return msgHandlers.taskResult(decodePayload(TaskResult, payload));
  }

  if (!VALID_MESSAGE_TYPES.includes(type)) {
    const serialized = JSON.stringify(envelope);
    const warning = `Unknown payload type ${type}. Discarding. ${serialized}`;
    console.warn(warning);

    captureStack();
    Sentry.captureMessage(warning, Sentry.Severity.Warning);
  }

  return undefined;
}

export { decodePayload, payloadRouter };
